import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import {
  getCancellationPrintSettings,
  printCancellationToKitchen,
  printBatchCancellationReceipt,
  type CancellationData,
} from "@/lib/cancellation-printing";
import type { SentOrderItem } from "@/hooks/useCurrentOrderItems";

interface CancelOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sentItems: SentOrderItem[];
  orderId: string | null;
  destination: { type: string; id?: string; number: string } | null;
  cancelSentItem: (itemId: string, reason?: string, skipReceiptPrint?: boolean) => Promise<boolean | undefined>;
  onSuccess: () => void;
}

export function CancelOrderDialog({
  open,
  onOpenChange,
  sentItems,
  orderId,
  destination,
  cancelSentItem,
  onSuccess,
}: CancelOrderDialogProps) {
  const [reason, setReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { activeRestaurant } = useRestaurantContext();

  const totalValue = sentItems.reduce((sum, item) => sum + Number(item.total_price), 0);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  const handleConfirm = async () => {
    if (!reason.trim()) {
      toast.error("Informe o motivo do cancelamento");
      return;
    }
    if (!orderId || !activeRestaurant || sentItems.length === 0) return;

    setIsProcessing(true);
    try {
      // Get user info for printing
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profileData } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user?.id || "")
        .single();
      const cancelledByName = profileData?.full_name || user?.email?.split("@")[0] || "Operador";

      // Get order info
      const { data: orderInfo } = await supabase
        .from("orders")
        .select("table_number, tab_number")
        .eq("id", orderId)
        .single();

      // 1. Cancel each item (register in order_item_cancellations), skip individual prints
      for (const item of sentItems) {
        await cancelSentItem(item.id, reason.trim(), true);
      }

      // 2. Mark order as cancelled
      await supabase
        .from("orders")
        .update({ status: "cancelled" as any, updated_at: new Date().toISOString() })
        .eq("id", orderId);

      // 3. Release table/command
      if (destination?.type === "table") {
        await supabase
          .from("tables")
          .update({ status: "available", current_order_id: null, updated_at: new Date().toISOString() })
          .eq("id", destination.id);
      } else if (destination?.type === "tab") {
        await supabase
          .from("commands")
          .update({ status: "available", current_order_id: null, customer_name: null, updated_at: new Date().toISOString() })
          .eq("id", destination.id);
      }

      // 4. Print consolidated cancellation receipt
      const printSettings = await getCancellationPrintSettings(activeRestaurant.id);

      const cancellationItems: CancellationData[] = sentItems.map((item) => ({
        productName: item.product_name,
        productId: item.product_id,
        quantity: item.quantity,
        unitPrice: Number(item.unit_price),
        totalPrice: Number(item.total_price),
        reason: reason.trim(),
        tableNumber: orderInfo?.table_number || undefined,
        tabNumber: orderInfo?.tab_number || undefined,
        cancelledBy: cancelledByName,
        restaurantId: activeRestaurant.id,
        restaurantName: activeRestaurant.name,
      }));

      // Print kitchen tickets for each item
      if (printSettings.printKitchen) {
        for (const cd of cancellationItems) {
          await printCancellationToKitchen(cd);
        }
      }

      // Print single consolidated receipt
      if (printSettings.printReceipt) {
        await printBatchCancellationReceipt(cancellationItems);
      }

      toast.success(`Pedido cancelado: ${sentItems.length} itens - ${formatCurrency(totalValue)}`);
      setReason("");
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error("[CancelOrder] Error:", error);
      toast.error("Erro ao cancelar pedido");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0b1020] border-[#1e2d52] text-[#c8dff5] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="h-5 w-5" />
            Cancelar Pedido
          </DialogTitle>
          <DialogDescription className="text-[#7aabdd]">
            Esta ação cancelará todos os itens enviados e liberará a {destination?.type === "tab" ? "comanda" : "mesa"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Summary */}
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-red-300">Itens a cancelar:</span>
              <span className="text-lg font-bold text-red-400">{sentItems.length}</span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <span className="text-sm text-red-300">Valor total:</span>
              <span className="text-lg font-bold text-red-400">{formatCurrency(totalValue)}</span>
            </div>
          </div>

          {/* Items list preview */}
          <div className="max-h-32 overflow-y-auto space-y-1">
            {sentItems.map((item) => (
              <div key={item.id} className="flex justify-between text-xs text-[#7aabdd] px-1">
                <span>{item.quantity}x {item.product_name}</span>
                <span>{formatCurrency(Number(item.total_price))}</span>
              </div>
            ))}
          </div>

          {/* Reason */}
          <div>
            <label className="text-sm font-medium text-[#7aabdd] mb-1 block">
              Motivo do cancelamento *
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Informe o motivo do cancelamento..."
              className="bg-[#111829] border-[#1e2d52] text-[#c8dff5] placeholder:text-[#3a5a8c] min-h-[80px]"
              autoFocus
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 bg-[#111829] border-[#1e2d52] text-[#7aabdd] hover:bg-[#1e2d52]"
              onClick={() => onOpenChange(false)}
              disabled={isProcessing}
            >
              Voltar
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white border-0"
              onClick={handleConfirm}
              disabled={isProcessing || !reason.trim()}
            >
              {isProcessing ? "Cancelando..." : "Confirmar Cancelamento"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
